import { AvailableExamsService } from '../../../../api/api-services/available-exams.service';
import { MyOskLectureGroupsService } from '../../../../api/api-services/my-osk-lecture-groups.service';
import { MyOskLectureGroupService } from '../../../../api/api-services/my-osk-lecture-group.service';
import { IMyOskLectureGroupVm } from '../../../../api/my-osk-lecture-group-vm.model';
import { MyOskEmployeesService } from '../../../../api/api-services/my-osk-employees.service';
import { isTeachingRole, OSK_EMPLOYEE_ROLE_LABELS } from '../../../../api/osk-employee-role.model';
import { SortOrder } from '../../../../api/sort-order.model';
import { OskInvitationToJoinService } from '../../../../api/api-services/osk-invitation-to-join.service';
import { IMyOskStudentCheckStatusResultVm } from '../../../../api/my-osk-student-check-status-result-vm.model';
import { MyOskStudentInvitationService } from '../../../../api/api-services/my-osk-student-invitation.service';
import { MyOskStudentStatusService } from '../../../../api/api-services/my-osk-student-status.service';
import { IMyOskStudentInvitationIm } from '../../../../api/my-osk-student-invitation-im.model';
import { IMyOskSubscriptonsVm } from '../../../../api/my-osk-subscriptons-vm.model';
import {
  catchError,
  debounceTime,
  EMPTY,
  filter,
  finalize,
  map,
  merge,
  Observable,
  Subject,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormBuilder,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { eModalRoles } from '../../../shared/models/modals';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import dayjs from 'dayjs';
import { MessageService } from 'primeng/api';
import { IOskInvitationToJoinIm } from '../../../../api/osk-invitation-to-join-im.model';
import {
  StudentDocumentData,
  StudentDocumentsPdfService,
} from '../../../shared/services/student-documents-pdf.service';
import { MyOskAgreementPreviewService } from '../../../../api/api-services/my-osk-agreement-preview.service';
import { AgreementSelection } from '../../../shared/components/agreement-picker/agreement-picker.component';
import { AgreementPdfService } from '../../../shared/services/agreement-pdf.service';
import { agreementApiError, agreementMessage } from '../../../shared/helpers/agreement-api-error';

/**
 * What the "E-mail" step knows about the typed address. The account on the platform is created for an e-mail, so
 * the school learns first whether the student already has one:
 * - `new` - no account, one is created and the student gets an activation link,
 * - `invite` / `inviteActive` - an account of someone outside the OSK (without / with access of their own): it gets
 *   an invitation and joins only when the student accepts it; until then the OSK sees nothing of it,
 * - `member` / `memberActive` - a student of the OSK already (e.g. joined with the OSK's book): no invitation,
 * - `pending` - the OSK's invitation is still waiting for the student's answer,
 * - `notStudent` - an account of OSK staff or of the platform,
 * - `throttled` - too many lookups in a short time.
 */
export type StudentLookupState =
  | 'idle'
  | 'invalid'
  | 'checking'
  | 'new'
  | 'invite'
  | 'inviteActive'
  | 'member'
  | 'memberActive'
  | 'pending'
  | 'notStudent'
  | 'throttled'
  | 'error';

/** An address with something before the @, a domain and a top-level domain of at least two letters. */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)*\.[^\s@.]{2,}$/;
/** The lookup starts this long after the last key, so a school that waits after typing gets the answer anyway. */
const EMAIL_LOOKUP_DEBOUNCE_MS = 500;

@Component({
  selector: 'app-add-student',
  templateUrl: './add-student.component.html',
  styleUrl: './add-student.component.scss',
})
export class AddStudentComponent implements OnInit, OnDestroy {
  private _myOskStudentStatusService = inject(MyOskStudentStatusService);
  private _oskInvitationToJoinService = inject(OskInvitationToJoinService)
  private _myOskStudentInvitationService = inject(
    MyOskStudentInvitationService
  );
  private _messageService = inject(MessageService);
  private _dialogRef = inject(DynamicDialogRef);
  private _fb = inject(FormBuilder);
  private _studentDocumentsPdfService = inject(StudentDocumentsPdfService);
  private _availableExamsService = inject(AvailableExamsService);
  private _myOskLectureGroupsService = inject(MyOskLectureGroupsService);
  private _myOskLectureGroupService = inject(MyOskLectureGroupService);
  private _myOskEmployeesService = inject(MyOskEmployeesService);
  private _myOskAgreementPreviewService = inject(MyOskAgreementPreviewService);
  private _agreementPdfService = inject(AgreementPdfService);

  missingSubscriptionMessage = signal<string|null>(null);
  selectedSubscription = signal<IMyOskSubscriptonsVm[]>([]);

  public activeStep = 0;
  /** The address the account is created for or looked up by - the first step, before any other data. */
  public emailControl = new FormControl('', { nonNullable: true });
  public lookupState: StudentLookupState = 'idle';
  /** The deadline of the invitation waiting for the student's answer (`pending`), DD.MM.YYYY. */
  public pendingInvitationExpires: string | null = null;
  public loading: boolean = false;
  public documentLoading: 'agreement' | 'rodo' | 'oskAgreement' | null = null;
  /** Course and agreement picked in the "Dokumenty" step; the agreement is saved for the student on creation. */
  public agreementSelection: AgreementSelection = { agreementGuid: null, courseGuid: null };
  public createdStudentData: StudentDocumentData | null = null;
  public dynamicForm!: FormGroup;
  public formFields: {
    name: string; // keyof IMyOskStudentInvitationIm
    label: string;
    type: 'input-string' | 'input-number' | 'input-mask' | 'dropdown' | 'lecture-group' | 'teacher';
    mask?: string;
    placeholder?: string;
    /** Small note rendered under the label. */
    hint?: string;
  }[] = [
    // Two columns on a wide screen: the fields go in pairs (name, contact, PKK with its category, assignment, address).
    { name: 'firstName', label: 'Imię', type: 'input-string' },
    { name: 'lastName', label: 'Nazwisko', type: 'input-string' },
    {
      name: 'phone',
      label: 'Numer telefonu',
      type: 'input-mask',
      mask: '999-999-999',
    },
    { name: 'pesel', label: 'Pesel', type: 'input-string' },
    { name: 'pkk', label: 'PKK', type: 'input-string' },
    {
      name: 'examCategoryGuid',
      label: 'Kategoria',
      type: 'dropdown',
      placeholder: 'Wybierz kategorię',
      hint: '(można zmienić później)',
    },
    {
      name: 'lectureGroupGuid',
      label: 'Grupa wykładowa',
      type: 'lecture-group',
      placeholder: 'Wybierz grupę (opcjonalnie)',
      hint: '(tylko otwarte grupy)',
    },
    {
      name: 'assignedEmployeeGuid',
      label: 'Prowadzący',
      type: 'teacher',
      placeholder: 'Instruktor lub wykładowca (opcjonalnie)',
      hint: '(można zmienić później)',
    },
    // Optional registered address - stored with the student and filled into the agreements.
    { name: 'street', label: 'Ulica i numer', type: 'input-string', hint: '(adres do umowy)' },
    { name: 'postalCode', label: 'Kod pocztowy', type: 'input-mask', mask: '99-999' },
    { name: 'city', label: 'Miasto', type: 'input-string' },
  ];
  /** Driving licence categories for the "Kategoria" dropdown; category B is preselected. */
  public examCategoryOptions: { label: string; value: string }[] = [];
  /** Open (not archived) lecture groups of the OSK the new student can join right away. */
  public lectureGroupOptions: { label: string; value: string }[] = [];
  /** Instructors and lecturers of the OSK the new student can be assigned to right away. */
  public teacherOptions: { label: string; value: string }[] = [];
  /** The group field shows a name input instead of the list while a new group is being created. */
  public creatingLectureGroup = false;
  public savingLectureGroup = false;
  public newLectureGroupName = new FormControl('', { nonNullable: true });
  public newLectureGroupError: string | null = null;

  private subscriptions = new Subscription();
  /** "Szukaj kursanta" and Enter - look the address up without waiting for the pause in typing. */
  private searchRequests$ = new Subject<void>();
  /** The address `lookupState` belongs to; null while the address is being typed. */
  private lookupEmail: string | null = null;

  get fc(): { [key: string]: FormControl } {
    return this.dynamicForm.controls as { [key: string]: FormControl };
  }

  /** The account exists - nothing is created and the personal data typed in the form does not change it. */
  get accountExists(): boolean {
    return this.lookupState === 'invite' || this.lookupState === 'member';
  }

  /** The account is outside the OSK: the form ends with an invitation the student has to accept. */
  get sendsInvitation(): boolean {
    return this.lookupState === 'invite';
  }

  /** The other data is filled in only once it is known that an account is created, invited or already a member. */
  get canEnterStudentData(): boolean {
    return this.lookupState === 'new' || this.accountExists;
  }

  ngOnInit(): void {
    this.buildForm();
    this.subscribeToEmailLookup();
    this.loadExamCategories();
    this.loadLectureGroups();
    this.loadTeachers();
  }

  /** A new group gets the category chosen for the student. */
  get newLectureGroupCategory(): string | null {
    const examGuid = this.fc['examCategoryGuid'].value;
    return this.examCategoryOptions.find((option) => option.value === examGuid)?.label ?? null;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  startLectureGroupCreation(): void {
    this.newLectureGroupName.reset();
    this.newLectureGroupError = null;
    this.creatingLectureGroup = true;
  }

  cancelLectureGroupCreation(): void {
    if (this.savingLectureGroup) return;
    this.creatingLectureGroup = false;
    this.newLectureGroupError = null;
  }

  createLectureGroup(): void {
    if (this.savingLectureGroup) return;

    const name = this.newLectureGroupName.value.trim();
    const examGuid = this.fc['examCategoryGuid'].value;
    if (!name) {
      this.newLectureGroupError = 'Podaj nazwę grupy.';
      return;
    }
    if (!examGuid) {
      this.newLectureGroupError = 'Najpierw wybierz kategorię - grupa zostanie utworzona dla niej.';
      return;
    }

    this.newLectureGroupError = null;
    this.savingLectureGroup = true;
    this.subscriptions.add(
      this._myOskLectureGroupService
        .post({ name, examGuid, studentGuids: [] })
        .pipe(finalize(() => (this.savingLectureGroup = false)))
        .subscribe({
          next: (group) => {
            this.lectureGroupOptions = [...this.lectureGroupOptions, this.toLectureGroupOption(group)];
            this.fc['lectureGroupGuid'].setValue(group.guid);
            this.creatingLectureGroup = false;
            this._messageService.add({
              severity: 'success',
              summary: 'Sukces',
              detail: `Grupa „${group.name}” została utworzona.`,
            });
          },
          error: (error: HttpErrorResponse) => {
            const apiMessage = typeof error.error === 'string' ? error.error : error.error?.message;
            this.newLectureGroupError = apiMessage?.includes('Group name')
              ? 'Podaj poprawną nazwę grupy (najwyżej 200 znaków).'
              : 'Nie udało się utworzyć grupy. Spróbuj ponownie.';
          },
        })
    );
  }

  closeDialog(status: eModalRoles = eModalRoles.CLOSE): void {
    this._dialogRef.close(status);
  }

  sendInvitation(): void {
    const subscriptionPlan = this.selectedSubscription()[0]?.planGuid;
    const isMember = this.lookupState === 'member';
    // The agreement is filled with the student's data, which the OSK gets only after the invitation is accepted.
    const savesAgreement = !this.sendsInvitation;

    this.loading = true;

    const payload: IMyOskStudentInvitationIm = {
      email: this.studentEmail,
      phone: this.fc['phone'].value || '',
      firstName: this.fc['firstName'].value || '',
      lastName: this.fc['lastName'].value || '',
      pesel: this.fc['pesel'].value || '',
      pkk: this.fc['pkk'].value || '',
      subscriptionPlanGuid: subscriptionPlan ?? undefined,
      examCategoryGuid: this.fc['examCategoryGuid'].value || undefined,
      lectureGroupGuid: this.fc['lectureGroupGuid'].value || undefined,
      assignedEmployeeGuids: this.fc['assignedEmployeeGuid'].value ? [this.fc['assignedEmployeeGuid'].value] : undefined,
      street: this.fc['street'].value || undefined,
      postalCode: this.fc['postalCode'].value || undefined,
      city: this.fc['city'].value || undefined,
      agreementGuid: savesAgreement ? this.agreementSelection.agreementGuid ?? undefined : undefined,
      courseGuid: savesAgreement ? this.agreementSelection.courseGuid ?? undefined : undefined,
    };

    this._myOskStudentInvitationService.post(payload).subscribe({
      next: (res) => {
        if(!res.success){
          this.handleError(agreementMessage(res.message, res.message))
        } else if (res.invitationSent) {
          if (res.message) {
            this._messageService.add({ severity: 'warn', summary: 'Uwaga', detail: res.message, life: 10000 });
          }
          this.handleSuccess('Zaproszenie wysłane. Kursant dołączy do Twojego OSK, gdy je zaakceptuje.');
        } else {
          if (res.agreementWarning) {
            this._messageService.add({
              severity: 'warn',
              summary: 'Umowa nie została zapisana',
              detail: 'Kursant został dodany, ale nie udało się zapisać umowy. Wygeneruj ją w szczegółach kursanta (zakładka „Umowy”).',
              life: 10000,
            });
          }
          this.handleSuccess(isMember
            ? 'Zapisano zmiany kursanta.'
            : 'Konto kursanta zostało utworzone - dostał e-mail z linkiem aktywacyjnym.');
        }
    },
      error: (err) => this.handleError(
        err?.error?.message ?? 'Wystąpił błąd. Spróbuj ponownie później.'
      ),
    });
  }

  goToDocuments(nextCallback: any): void {
    this.missingSubscriptionMessage.set(null);
    this.createdStudentData = {
      phone: this.fc['phone'].value || '',
      firstName: this.fc['firstName'].value || '',
      lastName: this.fc['lastName'].value || '',
      pesel: this.fc['pesel'].value || '',
      pkk: this.fc['pkk'].value || '',
      street: this.fc['street'].value || '',
      postalCode: this.fc['postalCode'].value || '',
      city: this.fc['city'].value || '',
    };
    nextCallback.emit();
  }

  async downloadAgreement(): Promise<void> {
    if (!this.createdStudentData) return;
    this.documentLoading = 'agreement';
    try {
      await this._studentDocumentsPdfService.downloadAgreement(this.createdStudentData);
    } catch {
      this.handleDocumentError();
    } finally {
      this.documentLoading = null;
    }
  }

  /** The chosen OSK agreement filled with the data from the form - the student does not exist yet. */
  downloadChosenAgreement(): void {
    const agreementGuid = this.agreementSelection.agreementGuid;
    if (!agreementGuid || this.documentLoading !== null) return;

    this.documentLoading = 'oskAgreement';
    this._myOskAgreementPreviewService
      .post({
        agreementGuid,
        courseGuid: this.agreementSelection.courseGuid ?? undefined,
        examCategoryGuid: this.fc['examCategoryGuid'].value || undefined,
        firstName: this.fc['firstName'].value || '',
        lastName: this.fc['lastName'].value || '',
        pesel: this.fc['pesel'].value || '',
        pkk: this.fc['pkk'].value || '',
        phone: this.fc['phone'].value || '',
        email: this.studentEmail,
        street: this.fc['street'].value || '',
        postalCode: this.fc['postalCode'].value || '',
        city: this.fc['city'].value || '',
      })
      .pipe(
        switchMap((document) =>
          this._agreementPdfService.download(document.title, document.html, [
            this.fc['firstName'].value,
            this.fc['lastName'].value,
          ])
        ),
        finalize(() => (this.documentLoading = null))
      )
      .subscribe({
        error: (error) =>
          this._messageService.add({
            severity: 'error',
            summary: 'Błąd',
            detail: agreementApiError(error, 'Nie udało się przygotować umowy PDF. Spróbuj ponownie.'),
          }),
      });
  }

  async downloadRodo(): Promise<void> {
    if (!this.createdStudentData) return;
    this.documentLoading = 'rodo';
    try {
      await this._studentDocumentsPdfService.downloadRodo(this.createdStudentData);
    } catch {
      this.handleDocumentError();
    } finally {
      this.documentLoading = null;
    }
  }

  /** "Szukaj kursanta" or Enter in the e-mail field. */
  searchStudent(): void {
    this.searchRequests$.next();
  }

  goToStudentData(nextCallback: any): void {
    if (this.canEnterStudentData) nextCallback.emit();
  }

  goToSubscriptions(nextCallback: any): void {
    // A typed but unconfirmed group name must not be dropped silently.
    if (this.creatingLectureGroup) {
      if (this.newLectureGroupName.value.trim()) {
        this.newLectureGroupError = 'Zatwierdź nową grupę albo anuluj jej tworzenie.';
        return;
      }
      this.cancelLectureGroupCreation();
    }
    nextCallback.emit();
  }

  trimInput(controlName: string): void {
    const control = this.fc[controlName];
    if (control && control.value && typeof control.value === 'string') {
      control.setValue(control.value.trim(), { emitEvent: false });
    }
  }

  private get studentEmail(): string {
    return this.emailControl.value.trim();
  }

  /**
   * Looks the typed address up once the school stops typing, or right away on "Szukaj kursanta". A key press hides
   * the previous answer at once, so the answer never belongs to another address than the one in the field.
   */
  private subscribeToEmailLookup(): void {
    const typing$ = this.emailControl.valueChanges.pipe(
      tap(() => this.forgetLookup()),
      debounceTime(EMAIL_LOOKUP_DEBOUNCE_MS)
    );

    this.subscriptions.add(
      merge(typing$, this.searchRequests$)
        .pipe(
          map(() => this.studentEmail),
          filter((email) => this.needsLookup(email)),
          switchMap((email) => this.lookUpStudent$(email))
        )
        .subscribe()
    );
  }

  private forgetLookup(): void {
    this.lookupEmail = null;
    this.pendingInvitationExpires = null;
    // A format error stays until the next check instead of blinking on every key.
    if (this.lookupState !== 'invalid') this.lookupState = 'idle';
  }

  /** Shows what is wrong with the typed text; false when there is nothing (new) to ask the API about. */
  private needsLookup(email: string): boolean {
    if (!EMAIL_PATTERN.test(email)) {
      this.lookupState = email ? 'invalid' : 'idle';
      return false;
    }
    // The answer for this address is already shown or on its way.
    return email !== this.lookupEmail || this.lookupState === 'error' || this.lookupState === 'throttled';
  }

  private lookUpStudent$(email: string): Observable<IMyOskStudentCheckStatusResultVm> {
    this.lookupEmail = email;
    this.lookupState = 'checking';
    // Only the e-mail - the account on the platform is created for it, a phone number does not identify it.
    return this._myOskStudentStatusService.get(email, '').pipe(
      tap((status) => {
        if (this.lookupEmail === email) this.applyStudentStatus(status);
      }),
      catchError((error: HttpErrorResponse) => {
        // The API limits the lookups of one OSK user, so a list of addresses cannot be checked through the panel.
        if (this.lookupEmail === email) this.lookupState = error?.status === 429 ? 'throttled' : 'error';
        return EMPTY;
      })
    );
  }

  private applyStudentStatus(status: IMyOskStudentCheckStatusResultVm): void {
    if (!status.accountExists) {
      this.lookupState = 'new';
    } else if (status.canBeStudent === false) {
      this.lookupState = 'notStudent';
    } else if (status.isMemberOfThisOsk) {
      this.lookupState = status.hasActiveAccess ? 'memberActive' : 'member';
    } else if (status.pendingInvitationExpiresUtc) {
      this.lookupState = 'pending';
      this.pendingInvitationExpires = dayjs(status.pendingInvitationExpiresUtc).format('DD.MM.YYYY');
    } else {
      this.lookupState = status.hasActiveAccess ? 'inviteActive' : 'invite';
    }
  }

  private loadExamCategories(): void {
    this._availableExamsService.get().subscribe({
      next: (exams) => {
        this.examCategoryOptions = exams
          .filter((exam) => exam.active)
          .map((exam) => ({ label: exam.examName, value: exam.guid }));
        const defaultCategory = exams.find(
          (exam) => exam.active && exam.examName.trim().toUpperCase() === 'B'
        );
        const control = this.fc['examCategoryGuid'];
        if (defaultCategory && !control.value) {
          control.setValue(defaultCategory.guid, { emitEvent: false });
        }
      },
      error: () =>
        this._messageService.add({
          severity: 'warn',
          summary: 'Uwaga',
          detail:
            'Nie udało się pobrać listy kategorii. Kursant wybierze kategorię przy pierwszym egzaminie.',
        }),
    });
  }

  private loadLectureGroups(): void {
    this._myOskLectureGroupsService.get(false, '', 'name', 1, 200, SortOrder.Ascending).subscribe({
      next: (groups) => {
        this.lectureGroupOptions = (groups.data ?? []).map((group) => this.toLectureGroupOption(group));
      },
      // The group is optional - a failed list only hides the choice.
      error: () => (this.lectureGroupOptions = []),
    });
  }

  private loadTeachers(): void {
    this._myOskEmployeesService.get().subscribe({
      next: (employees) => {
        this.teacherOptions = employees
          .filter((employee) => !!employee.guid && isTeachingRole(employee.role))
          .map((employee) => ({
            label: `${employee.displayName} (${OSK_EMPLOYEE_ROLE_LABELS[employee.role]})`,
            value: employee.guid!,
          }));
      },
      // The assignment is optional - a failed list only hides the choice.
      error: () => (this.teacherOptions = []),
    });
  }

  private toLectureGroupOption(group: IMyOskLectureGroupVm): { label: string; value: string } {
    return { label: `${group.name} (kat. ${group.examName})`, value: group.guid };
  }

  private buildForm(): void {
    const formGroup: any = {};
    this.formFields.forEach((field) => {
      formGroup[field.name] = [null, { updateOn: 'blur' }];
    });
    this.dynamicForm = this._fb.group(formGroup);
  }

  /** An account with access of its own gets only the invitation - it needs no subscription, group or data. */
  inviteStudentWithAccess() {
    this.loading=true
    const dataToPost: IOskInvitationToJoinIm = {
      email: this.studentEmail
    }
    this._oskInvitationToJoinService.post(dataToPost).subscribe({
      next: (res) => {
        if (!res.success) {
          this.handleError(res.message)
        } else {
          this.handleSuccess(res.message || 'Zaproszenie wysłane. Kursant dołączy do Twojego OSK, gdy je zaakceptuje.')
        }
      }, error: (err) => {
        this.handleError(
          err?.error?.message ?? 'Wystąpił błąd. Spróbuj ponownie później.'
        )
    }});
  }

  private handleSuccess(message: string): void {
    this.loading = false;
    this._messageService.add({
      severity: 'success',
      summary: 'Sukces',
      detail: message,
    });
    this.closeDialog(eModalRoles.SUCCESS);
  }

  private handleError(message: string): void {
    this.loading = false;
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: message,
    });
  }

  private handleDocumentError(): void {
    this._messageService.add({
      severity: 'error',
      summary: 'Błąd',
      detail: 'Nie udało się przygotować dokumentu PDF. Spróbuj ponownie.',
    });
  }
}
